import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Row, Col, Image, Spinner, Tabs, Tab, InputGroup, Card, Alert, Badge, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useEntidadeContext } from '../../EntidadeContext';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const LandPageServicosTab = ({ initialServicos = [], isLoading }) => {
    const { currentEntidade } = useEntidadeContext();
    const [configs, setConfigs] = useState({});
    const [isSaving, setIsSaving] = useState({});
    const [expandedId, setExpandedId] = useState(null);

    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [aspect, setAspect] = useState(16 / 9);
    const [cropContext, setCropContext] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        const initialData = {};
        initialServicos.forEach(servico => {
            const marketingConfig = servico.marketing_config;
            const initialImagensAntesDepois = (marketingConfig?.imagens_antes_depois || []).map((img, index) => ({
                id: img.id || Date.now() + index,
                antes: { file: null, preview: img.antes_path ? `/storage/${img.antes_path}` : null },
                depois: { file: null, preview: img.depois_path ? `/storage/${img.depois_path}` : null },
            }));
            const processedContentBlocks = (marketingConfig?.content_blocks || []).map(block => {
                if (block.type === 'image') {
                    const newBlock = { ...block };
                    if (block.src_desktop?.path) newBlock.src_desktop.preview = `/storage/${block.src_desktop.path}`;
                    if (block.src_mobile?.path) newBlock.src_mobile.preview = `/storage/${block.src_mobile.path}`;
                    return newBlock;
                }
                if (block.type === 'form') {
                    return { ...block, titulo: block.titulo || 'Comece sua transformação', descricao: block.descricao || 'Preencha o formulário para agendar sua avaliação.' };
                }
                return block;
            });
            initialData[servico.id] = {
                titulo_hero: marketingConfig?.titulo_hero || '',
                subtitulo_hero: marketingConfig?.subtitulo_hero || '',
                texto_cta_principal: marketingConfig?.texto_cta_principal || '',
                redirect_url: marketingConfig?.redirect_url || '',
                imagem_hero_preview: marketingConfig?.imagem_hero_path ? `/storage/${marketingConfig.imagem_hero_path}` : null,
                titulo_beneficios: marketingConfig?.titulo_beneficios || '',
                lista_beneficios: marketingConfig?.lista_beneficios || [],
                titulo_faq: marketingConfig?.titulo_faq || '',
                lista_faq: marketingConfig?.lista_faq || [],
                titulo_antes_depois: marketingConfig?.titulo_antes_depois || '',
                lista_antes_depois: initialImagensAntesDepois,
                titulo_oferta: marketingConfig?.titulo_oferta || '',
                preco_antigo: marketingConfig?.preco_antigo || '',
                preco_novo: marketingConfig?.preco_novo || '',
                texto_urgencia: marketingConfig?.texto_urgencia || '',
                titulo_pagina: marketingConfig?.titulo_pagina || '',
                meta_descricao: marketingConfig?.meta_descricao || '',
                template_slug: 'video_hero',
                content_blocks: processedContentBlocks,
                file: null,
            };
        });
        setConfigs(initialData);
    }, [initialServicos]);

    const handleChange = (servicoId, field, value) => {
        setConfigs(prev => ({ ...prev, [servicoId]: { ...prev[servicoId], [field]: value } }));
    };

    const handleListChange = (servicoId, listName, index, field, value) => {
        const newList = [...configs[servicoId][listName]];
        newList[index][field] = value;
        handleChange(servicoId, listName, newList);
    };

    const addListItem = (servicoId, listName) => {
        const newItem = listName === 'lista_beneficios' ? { icone: '✅', titulo: '', descricao: '' } : { pergunta: '', resposta: '' };
        handleChange(servicoId, listName, [...configs[servicoId][listName], newItem]);
    };

    const removeListItem = (servicoId, listName, index) => {
        handleChange(servicoId, listName, configs[servicoId][listName].filter((_, i) => i !== index));
    };

    const handleImagePairChange = (servicoId, pairId, side, file) => {
        if (!file) return;
        const newList = configs[servicoId].lista_antes_depois.map(pair =>
            pair.id === pairId ? { ...pair, [side]: { file, preview: URL.createObjectURL(file) } } : pair
        );
        handleChange(servicoId, 'lista_antes_depois', newList);
    };

    const addImagePair = (servicoId) => {
        handleChange(servicoId, 'lista_antes_depois', [...configs[servicoId].lista_antes_depois, { id: Date.now(), antes: { file: null, preview: null }, depois: { file: null, preview: null } }]);
    };

    const removeImagePair = (servicoId, pairId) => {
        handleChange(servicoId, 'lista_antes_depois', configs[servicoId].lista_antes_depois.filter(pair => pair.id !== pairId));
    };

    const handleFileChange = (servicoId, e) => {
        const file = e.target.files[0];
        if (file) setConfigs(prev => ({ ...prev, [servicoId]: { ...prev[servicoId], file, imagem_hero_preview: URL.createObjectURL(file) } }));
    };

    const handleBlockChange = (servicoId, blockId, field, value) => {
        const newBlocks = configs[servicoId].content_blocks.map(block => block.id === blockId ? { ...block, [field]: value } : block);
        handleChange(servicoId, 'content_blocks', newBlocks);
    };

    const handleCropComplete = useCallback(async () => {
        if (!completedCrop || !imgRef.current || !cropContext) return;
        const { servicoId, blockId, field, originalFile } = cropContext;
        const canvas = document.createElement('canvas');
        const image = imgRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = completedCrop.width * scaleX;
        canvas.height = completedCrop.height * scaleY;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, completedCrop.x * scaleX, completedCrop.y * scaleY, completedCrop.width * scaleX, completedCrop.height * scaleY, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
            if (!blob) return;
            const croppedFile = new File([blob], originalFile.name, { type: originalFile.type });
            const newBlocks = configs[servicoId].content_blocks.map(block =>
                block.id === blockId ? { ...block, [field]: { file: croppedFile, preview: URL.createObjectURL(croppedFile) } } : block
            );
            handleChange(servicoId, 'content_blocks', newBlocks);
            setShowCropper(false);
            setImageToCrop(null);
        }, originalFile.type, 0.95);
    }, [completedCrop, imgRef, cropContext, configs, handleChange]);

    const handleOpenCropper = useCallback((servicoId, blockId, field, file) => {
        if (!file) return;
        setAspect(field === 'src_desktop' ? 1430 / 700 : 640 / 1024);
        setImageToCrop(URL.createObjectURL(file));
        setCropContext({ servicoId, blockId, field, originalFile: file });
        setShowCropper(true);
    }, []);

    const onImageLoad = (e) => {
        const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
        setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height));
    };

    const addContentBlock = (servicoId, type) => {
        const currentBlocks = configs[servicoId].content_blocks;
        const limits = { image: 10, video: 3, form: 2 };
        if (limits[type] && currentBlocks.filter(b => b.type === type).length >= limits[type]) return;
        const newBlock = {
            id: `block-${Date.now()}`, type,
            src_desktop: type === 'image' ? { file: null, preview: 'https://placehold.co/120x80/EFEFEF/333?text=1430x700' } : undefined,
            src_mobile: type === 'image' ? { file: null, preview: 'https://placehold.co/88x132/EFEFEF/333?text=640x1024' } : undefined,
            src: type === 'video' ? '' : undefined,
            formId: type === 'form' ? `form-${Date.now()}` : undefined,
            titulo: type === 'form' ? 'Comece sua transformação' : undefined,
            descricao: type === 'form' ? 'Preencha o formulário para agendar sua avaliação.' : undefined,
            sectionBgColor: '#FFFFFF',
        };
        handleChange(servicoId, 'content_blocks', [...currentBlocks, newBlock]);
    };

    const removeContentBlock = (servicoId, blockId) => {
        handleChange(servicoId, 'content_blocks', configs[servicoId].content_blocks.filter(b => b.id !== blockId));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const servicoId = result.destination.droppableId;
        const items = Array.from(configs[servicoId].content_blocks);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        handleChange(servicoId, 'content_blocks', items);
    };

    const handleSave = async (servicoId) => {
        setIsSaving(prev => ({ ...prev, [servicoId]: true }));
        const formData = new FormData();
        const config = configs[servicoId];
        config.content_blocks.forEach((block, index) => {
            if (block.type === 'image') {
                if (block.src_desktop?.file) formData.append(`content_block_files[${index}][src_desktop_file]`, block.src_desktop.file);
                if (block.src_mobile?.file) formData.append(`content_block_files[${index}][src_mobile_file]`, block.src_mobile.file);
            }
        });
        formData.append('titulo_hero', config.titulo_hero);
        formData.append('subtitulo_hero', config.subtitulo_hero);
        formData.append('texto_cta_principal', config.texto_cta_principal);
        formData.append('redirect_url', config.redirect_url);
        formData.append('lista_beneficios', JSON.stringify(config.lista_beneficios));
        formData.append('titulo_beneficios', config.titulo_beneficios);
        formData.append('lista_faq', JSON.stringify(config.lista_faq));
        formData.append('titulo_faq', config.titulo_faq);
        formData.append('titulo_oferta', config.titulo_oferta);
        formData.append('preco_antigo', config.preco_antigo);
        formData.append('preco_novo', config.preco_novo);
        formData.append('texto_urgencia', config.texto_urgencia);
        formData.append('titulo_pagina', config.titulo_pagina);
        formData.append('meta_descricao', config.meta_descricao);
        formData.append('template_slug', config.template_slug);
        formData.append('titulo_antes_depois', config.titulo_antes_depois);
        formData.append('quantidade_imagens', config.content_blocks.filter(b => b.type === 'image').length);
        formData.append('content_blocks', JSON.stringify(config.content_blocks));
        if (config.file) formData.append('imagem_hero', config.file);
        config.lista_antes_depois.forEach((pair, index) => {
            if (pair.antes.file) formData.append(`imagens_antes_depois[${index}][antes_file]`, pair.antes.file);
            if (pair.depois.file) formData.append(`imagens_antes_depois[${index}][depois_file]`, pair.depois.file);
            if (typeof pair.id === 'number' && pair.id <= 1000000000) formData.append(`imagens_antes_depois[${index}][id]`, pair.id);
        });
        try {
            await axios.post(`/api/servicos/${servicoId}/marketing-config`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert('Configuração salva com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar:', error);
            alert('Erro ao salvar configuração.');
        } finally {
            setIsSaving(prev => ({ ...prev, [servicoId]: false }));
        }
    };

    const handleViewPage = (e, servico) => {
        e.stopPropagation();
        if (currentEntidade?.slug && servico.slug) {
            window.open(`/lp/${currentEntidade.slug}/${servico.slug}`, '_blank', 'noopener,noreferrer');
        }
    };

    if (isLoading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '12px' }}>
            <Spinner animation="border" variant="primary" size="sm" />
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>Carregando serviços...</span>
        </div>
    );

    const servicosAtivos = initialServicos.filter(s => s.ativo === 1);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <h2 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                        Landing Pages
                    </h2>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                        Configure as páginas de captura de leads para cada serviço
                    </p>
                </div>

                {servicosAtivos.length === 0 ? (
                    <div style={{
                        textAlign: 'center', padding: '60px 24px',
                        backgroundColor: '#f9fafb', borderRadius: '12px',
                        border: '2px dashed #e5e7eb',
                    }}>
                        <FontAwesomeIcon icon="globe" style={{ fontSize: '40px', color: '#d1d5db', marginBottom: '12px', display: 'block' }} />
                        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                            Nenhum serviço ativo
                        </h3>
                        <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                            Ative serviços para configurar suas landing pages
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {servicosAtivos.map(servico => (
                            configs[servico.id] && (
                                <div
                                    key={servico.id}
                                    style={{
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        border: `1px solid ${expandedId === servico.id ? '#bfdbfe' : '#e5e7eb'}`,
                                        overflow: 'hidden',
                                        boxShadow: expandedId === servico.id ? '0 0 0 2px #3b82f620' : 'none',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {/* Header do card */}
                                    <div
                                        onClick={() => setExpandedId(expandedId === servico.id ? null : servico.id)}
                                        style={{
                                            padding: '16px 20px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            backgroundColor: expandedId === servico.id ? '#f8faff' : '#fff',
                                            transition: 'background 0.15s',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '10px',
                                                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <FontAwesomeIcon icon="globe" style={{ color: '#fff', fontSize: '16px' }} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                                                    {servico.nome}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                                                    {servico.marketing_config
                                                        ? `${configs[servico.id]?.content_blocks?.length || 0} blocos configurados`
                                                        : 'Sem configuração'
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {/* Badge configurado */}
                                            {servico.marketing_config && (
                                                <span style={{
                                                    fontSize: '11px', fontWeight: '600',
                                                    padding: '3px 10px', borderRadius: '999px',
                                                    backgroundColor: '#dcfce7', color: '#16a34a',
                                                }}>
                                                    ✓ Configurado
                                                </span>
                                            )}

                                            {/* Botão Visualizar */}
                                            {servico.marketing_config && (
                                                <button
                                                    onClick={(e) => handleViewPage(e, servico)}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: '6px',
                                                        backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb',
                                                        color: '#374151', cursor: 'pointer',
                                                        fontSize: '12px', fontWeight: '500',
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon="eye" style={{ fontSize: '11px' }} />
                                                    Ver página
                                                </button>
                                            )}

                                            {/* Chevron */}
                                            <FontAwesomeIcon
                                                icon="chevron-down"
                                                style={{
                                                    fontSize: '12px', color: '#9ca3af',
                                                    transform: expandedId === servico.id ? 'rotate(180deg)' : 'rotate(0)',
                                                    transition: 'transform 0.2s',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Conteúdo expandido */}
                                    {expandedId === servico.id && (
                                        <div style={{ borderTop: '1px solid #f3f4f6' }}>
                                            <div style={{ padding: '20px' }}>
                                                <ServicoTabs
                                                    servicoId={servico.id}
                                                    config={configs[servico.id]}
                                                    handleChange={handleChange}
                                                    handleFileChange={handleFileChange}
                                                    handleListChange={handleListChange}
                                                    addListItem={addListItem}
                                                    removeListItem={removeListItem}
                                                    handleImagePairChange={handleImagePairChange}
                                                    addImagePair={addImagePair}
                                                    removeImagePair={removeImagePair}
                                                    addContentBlock={addContentBlock}
                                                    removeContentBlock={removeContentBlock}
                                                    handleBlockChange={handleBlockChange}
                                                    handleOpenCropper={handleOpenCropper}
                                                />
                                            </div>

                                            {/* Footer com botão salvar */}
                                            <div style={{
                                                padding: '16px 20px',
                                                borderTop: '1px solid #f3f4f6',
                                                backgroundColor: '#f9fafb',
                                                display: 'flex', justifyContent: 'flex-end',
                                            }}>
                                                <button
                                                    onClick={() => handleSave(servico.id)}
                                                    disabled={isSaving[servico.id]}
                                                    style={{
                                                        padding: '9px 20px',
                                                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                        color: '#fff', border: 'none', borderRadius: '8px',
                                                        cursor: isSaving[servico.id] ? 'not-allowed' : 'pointer',
                                                        fontSize: '13px', fontWeight: '600',
                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                        opacity: isSaving[servico.id] ? 0.7 : 1,
                                                        boxShadow: '0 1px 3px rgba(59,130,246,0.4)',
                                                    }}
                                                >
                                                    {isSaving[servico.id]
                                                        ? <><Spinner as="span" size="sm" animation="border" /> Salvando...</>
                                                        : <><FontAwesomeIcon icon="save" /> Salvar Configurações</>
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        ))}
                    </div>
                )}
            </div>

            {/* Modal de Corte */}
            <Modal show={showCropper} onHide={() => setShowCropper(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Modal.Title style={{ fontSize: '16px', fontWeight: '700' }}>Cortar Imagem</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {imageToCrop && (
                        <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} aspect={aspect}>
                            <img ref={imgRef} src={imageToCrop} onLoad={onImageLoad} alt="Crop" style={{ maxHeight: '70vh' }} />
                        </ReactCrop>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6' }}>
                    <button
                        onClick={() => setShowCropper(false)}
                        style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151', cursor: 'pointer', fontSize: '13px' }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleCropComplete}
                        style={{ padding: '8px 20px', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                    >
                        Aplicar Corte
                    </button>
                </Modal.Footer>
            </Modal>
        </DragDropContext>
    );
};

// ─── ServicoTabs — mantido igual ao original ──────────────────────────────────
const ServicoTabs = ({
    servicoId, config, handleChange, handleFileChange, handleListChange,
    addListItem, removeListItem, handleImagePairChange, addImagePair,
    removeImagePair, addContentBlock, removeContentBlock, handleBlockChange, handleOpenCropper
}) => {
    const activeBlockTypes = new Set(config.content_blocks.map(block => block.type));

    const renderAddBlockButton = (type, icon, label) => {
        const uniqueBlockTypes = ['conteudo_principal', 'beneficios', 'faq', 'antes_depois', 'oferta', 'seo'];
        const limitedBlockTypes = { image: 10, video: 3, form: 2 };
        let isDisabled = false;
        let tooltipMessage = 'Este bloco já foi adicionado.';
        if (uniqueBlockTypes.includes(type)) {
            isDisabled = activeBlockTypes.has(type);
        } else if (limitedBlockTypes[type]) {
            const count = config.content_blocks.filter(block => block.type === type).length;
            isDisabled = count >= limitedBlockTypes[type];
            tooltipMessage = `Limite de ${limitedBlockTypes[type]} blocos atingido.`;
        }
        const button = (
            <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => !isDisabled && addContentBlock(servicoId, type)} disabled={isDisabled} style={isDisabled ? { pointerEvents: 'none' } : {}}>
                <FontAwesomeIcon icon={icon} className="me-2" /> {label}
            </Button>
        );
        if (isDisabled) return <OverlayTrigger overlay={<Tooltip>{tooltipMessage}</Tooltip>}><span className="d-inline-block">{button}</span></OverlayTrigger>;
        return button;
    };

    return (
        <Tabs defaultActiveKey="estrutura" id={`config-tabs-${servicoId}`} className="mb-3">
            
            <Tab eventKey="estrutura" title="🏗️ Estrutura da Página">
                    <Alert variant="info">
                        <FontAwesomeIcon icon="info-circle" className="me-2" />
                        Arraste os blocos para reordenar a estrutura da sua Landing Page.
                    </Alert>
                    <Droppable droppableId={String(servicoId)}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {config?.content_blocks?.map((block, index) => (
                                    <Draggable key={block.id} draggableId={String(block.id)} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="mb-2">
                                                <Card style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                                                    <Card.Body style={{ padding: '12px 16px' }}>
                                                        <Row className="align-items-center">
                                                            <Col xs="auto" {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#9ca3af' }}>
                                                                <FontAwesomeIcon icon="grip-vertical" />
                                                            </Col>
                                                            <Col>
                                                                {block.type === 'image' && (
                                                                    <>
                                                                        <Badge bg="secondary" className="mb-2">Bloco de Imagem</Badge>
                                                                        <Row>
                                                                            <Col md={2}>
                                                                                <Form.Label style={{ fontSize: '12px' }}>Desktop</Form.Label>
                                                                                <Image src={block.src_desktop?.preview || 'https://placehold.co/120x80/EFEFEF/333?text=1430x700'} fluid thumbnail />
                                                                                <Form.Control className="mt-1" size="sm" type="file" accept="image/*" onChange={(e) => handleOpenCropper(servicoId, block.id, 'src_desktop', e.target.files[0])} />
                                                                            </Col>
                                                                            <Col md={2}>
                                                                                <Form.Label style={{ fontSize: '12px' }}>Mobile</Form.Label>
                                                                                <Image src={block.src_mobile?.preview || 'https://placehold.co/80x120/EFEFEF/333?text=640x1024'} fluid thumbnail />
                                                                                <Form.Control className="mt-1" size="sm" type="file" accept="image/*" onChange={(e) => handleOpenCropper(servicoId, block.id, 'src_mobile', e.target.files[0])} />
                                                                            </Col>
                                                                        </Row>
                                                                    </>
                                                                )}
                                                                {block.type === 'video' && (
                                                                    <>
                                                                        <Badge bg="danger" className="mb-2">Vídeo</Badge>
                                                                        <Form.Control size="sm" type="text" placeholder="URL do Vídeo (YouTube, Vimeo)" value={block.src || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'src', e.target.value)} />
                                                                    </>
                                                                )}
                                                                {block.type === 'form' && (
                                                                    <>
                                                                        <Badge bg="primary" className="mb-2">Formulário</Badge>
                                                                        <Form.Group className="mt-1">
                                                                            <Form.Label style={{ fontSize: '12px' }}>Título</Form.Label>
                                                                            <Form.Control size="sm" type="text" value={block.titulo || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'titulo', e.target.value)} />
                                                                        </Form.Group>
                                                                        <Form.Group className="mt-2">
                                                                            <Form.Label style={{ fontSize: '12px' }}>Descrição</Form.Label>
                                                                            <Form.Control as="textarea" rows={2} size="sm" value={block.descricao || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'descricao', e.target.value)} />
                                                                        </Form.Group>
                                                                    </>
                                                                )}
                                                                {block.type === 'faq' && <Badge bg="info">FAQ</Badge>}
                                                                {block.type === 'beneficios' && <Badge bg="success">Benefícios</Badge>}
                                                                {block.type === 'conteudo_principal' && <Badge bg="dark">Conteúdo Principal</Badge>}
                                                                {block.type === 'oferta' && <Badge bg="danger">Oferta</Badge>}
                                                                {block.type === 'antes_depois' && <Badge bg="warning" text="dark">Antes e Depois</Badge>}
                                                                {block.type === 'seo' && <Badge bg="secondary">SEO</Badge>}

                                                                <Form.Group as={Row} className="mt-2 align-items-center">
                                                                    <Form.Label column sm="auto" className="mb-0" style={{ fontSize: '12px' }}>Cor de Fundo:</Form.Label>
                                                                    <Col sm="auto">
                                                                        <Form.Control type="color" value={block.sectionBgColor} onChange={(e) => handleBlockChange(servicoId, block.id, 'sectionBgColor', e.target.value)} style={{ width: '36px', height: '28px', padding: '2px', borderRadius: '6px' }} />
                                                                    </Col>
                                                                    <Col>
                                                                        <Form.Control type="text" size="sm" value={block.sectionBgColor} onChange={(e) => handleBlockChange(servicoId, block.id, 'sectionBgColor', e.target.value)} />
                                                                    </Col>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col xs="auto">
                                                                <button
                                                                    onClick={() => removeContentBlock(servicoId, block.id)}
                                                                    style={{
                                                                        width: '30px', height: '30px', borderRadius: '8px',
                                                                        backgroundColor: '#fef2f2', border: '1px solid #fecaca',
                                                                        color: '#ef4444', cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontSize: '12px',
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon="trash" />
                                                                </button>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>

                    {/* Adicionar blocos */}
                    <div style={{
                        marginTop: '16px', padding: '12px 16px',
                        backgroundColor: '#f9fafb', borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                    }}>
                        <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>
                            Adicionar Bloco:
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {renderAddBlockButton('image', 'image', 'Imagem')}
                            {renderAddBlockButton('video', 'video', 'Vídeo')}
                            {renderAddBlockButton('form', 'file-alt', 'Formulário')}
                            {renderAddBlockButton('conteudo_principal', 'star', 'Principal')}
                            {renderAddBlockButton('beneficios', 'check-circle', 'Benefícios')}
                            {renderAddBlockButton('faq', 'question-circle', 'FAQ')}
                            {renderAddBlockButton('antes_depois', 'images', 'Antes/Depois')}
                            {renderAddBlockButton('oferta', 'tag', 'Oferta')}
                            {renderAddBlockButton('seo', 'cog', 'SEO')}
                        </div>
                    </div>
                </Tab>

            {activeBlockTypes.has('conteudo_principal') && (
                <Tab eventKey="hero" title="🌟 Conteúdo Principal">
                    <Form>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título Principal</Form.Label>
                                    <Form.Control type="text" value={config.titulo_hero} onChange={(e) => handleChange(servicoId, 'titulo_hero', e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Subtítulo</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={config.subtitulo_hero} onChange={(e) => handleChange(servicoId, 'subtitulo_hero', e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Texto do Botão (CTA)</Form.Label>
                                    <Form.Control type="text" value={config.textoprincipal} onChange={(e) => handleChange(servicoId, 'texto_cta_principal', e.target.value)} />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>URL de Redirecionamento</Form.Label>
                                    <Form.Control type="text" placeholder="https://..." value={config.redirect_url} onChange={(e) => handleChange(servicoId, 'redirect_url', e.target.value)} />
                                    <Form.Text muted>Se preenchido, o usuário será levado para esta URL após o envio.</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Imagem Principal</Form.Label>
                                    <div className="text-center mb-2">
                                        <Image src={config.imagem_hero_preview || 'https://via.placeholder.com/300x200?text=Sem+Imagem'} thumbnail style={{ maxHeight: '150px', width: 'auto' }} />
                                    </div>
                                    <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleFileChange(servicoId, e)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Tab>
            )}

            {activeBlockTypes.has('beneficios') && (
                <Tab eventKey="beneficios" title="✅ Benefícios">
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título da Seção</Form.Label>
                        <Form.Control type="text" value={config.titulo_beneficios} onChange={(e) => handleChange(servicoId, 'titulo_beneficios', e.target.value)} />
                    </Form.Group>
                    {config.lista_beneficios.map((item, index) => (
                        <Card key={index} className="mb-2" style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                            <Card.Body style={{ padding: '12px' }}>
                                <Row className="align-items-center">
                                    <Col xs="auto">
                                        <Form.Control style={{ width: '60px' }} type="text" placeholder="Ícone" value={item.icone} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'icone', e.target.value)} />
                                    </Col>
                                    <Col>
                                        <Form.Control className="mb-1" type="text" placeholder="Título" value={item.titulo} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'titulo', e.target.value)} />
                                        <Form.Control size="sm" type="text" placeholder="Descrição" value={item.descricao} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'descricao', e.target.value)} />
                                    </Col>
                                    <Col xs="auto">
                                        <button onClick={() => removeListItem(servicoId, 'lista_beneficios', index)} style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                                            <FontAwesomeIcon icon="trash" />
                                        </button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <button onClick={() => addListItem(servicoId, 'lista_beneficios')} style={{ padding: '7px 14px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon="plus" /> Adicionar Benefício
                    </button>
                </Tab>
            )}

            {activeBlockTypes.has('faq') && (
                <Tab eventKey="faq" title="❓ FAQ">
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título da Seção</Form.Label>
                        <Form.Control type="text" value={config.titulo_faq} onChange={(e) => handleChange(servicoId, 'titulo_faq', e.target.value)} />
                    </Form.Group>
                    {config.lista_faq.map((item, index) => (
                        <Card key={index} className="mb-2" style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                            <Card.Body style={{ padding: '12px' }}>
                                <Row>
                                    <Col>
                                        <Form.Control className="mb-2" type="text" placeholder="Pergunta" value={item.pergunta} onChange={(e) => handleListChange(servicoId, 'lista_faq', index, 'pergunta', e.target.value)} />
                                        <Form.Control as="textarea" rows={2} placeholder="Resposta" value={item.resposta} onChange={(e) => handleListChange(servicoId, 'lista_faq', index, 'resposta', e.target.value)} />
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center">
                                        <button onClick={() => removeListItem(servicoId, 'lista_faq', index)} style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                                            <FontAwesomeIcon icon="trash" />
                                        </button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <button onClick={() => addListItem(servicoId, 'lista_faq')} style={{ padding: '7px 14px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon="plus" /> Adicionar Pergunta
                    </button>
                </Tab>
            )}

            {activeBlockTypes.has('antes_depois') && (
                <Tab eventKey="social" title="⭐ Prova Social">
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título da Seção</Form.Label>
                        <Form.Control type="text" value={config.titulo_antes_depois} onChange={(e) => handleChange(servicoId, 'titulo_antes_depois', e.target.value)} />
                    </Form.Group>
                    {config.lista_antes_depois.map((pair) => (
                        <Card key={pair.id} className="mb-3" style={{ borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                            <Card.Body style={{ padding: '12px' }}>
                                <Row className="align-items-center">
                                    <Col md={5}>
                                        <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Antes</Form.Label>
                                        <Image src={pair.antes.preview || 'https://placehold.co/400x400/EFEFEF/333?text=Antes'} thumbnail className="mb-2" />
                                        <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleImagePairChange(servicoId, pair.id, 'antes', e.target.files[0])} />
                                    </Col>
                                    <Col md={5}>
                                        <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Depois</Form.Label>
                                        <Image src={pair.depois.preview || 'https://placehold.co/400x400/D1E7DD/333?text=Depois'} thumbnail className="mb-2" />
                                        <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleImagePairChange(servicoId, pair.id, 'depois', e.target.files[0])} />
                                    </Col>
                                    <Col md={2} className="text-center">
                                        <button onClick={() => removeImagePair(servicoId, pair.id)} style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                                            <FontAwesomeIcon icon="trash" />
                                        </button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <button onClick={() => addImagePair(servicoId)} style={{ padding: '7px 14px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon="plus" /> Adicionar Par (Antes/Depois)
                    </button>
                </Tab>
            )}

            {activeBlockTypes.has('oferta') && (
                <Tab eventKey="oferta" title="💲 Oferta">
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título da Seção</Form.Label>
                        <Form.Control type="text" value={config.titulo_oferta} onChange={(e) => handleChange(servicoId, 'titulo_oferta', e.target.value)} />
                    </Form.Group>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Preço Antigo (De)</Form.Label>
                                <Form.Control type="text" placeholder="Ex: R$ 1.500,00" value={config.preco_antigo} onChange={(e) => handleChange(servicoId, 'preco_antigo', e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Preço Novo (Por)</Form.Label>
                                <Form.Control type="text" placeholder="Ex: R$ 1.299,00" value={config.preco_novo} onChange={(e) => handleChange(servicoId, 'preco_novo', e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Texto de Urgência</Form.Label>
                        <Form.Control as="textarea" rows={2} value={config.texto_urgencia} onChange={(e) => handleChange(servicoId, 'texto_urgencia', e.target.value)} />
                    </Form.Group>
                </Tab>
            )}

            {activeBlockTypes.has('seo') && (
                <Tab eventKey="seo" title="⚙️ SEO">
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Título da Página (SEO)</Form.Label>
                        <Form.Control type="text" value={config.titulo_pagina} onChange={(e) => handleChange(servicoId, 'titulo_pagina', e.target.value)} />
                        <Form.Text muted>Aparece na aba do navegador e nos resultados do Google.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px', fontWeight: '600' }}>Meta Descrição (SEO)</Form.Label>
                        <Form.Control as="textarea" rows={3} value={config.meta_descricao} onChange={(e) => handleChange(servicoId, 'meta_descricao', e.target.value)} />
                        <Form.Text muted>Descrição curta (máx. 160 caracteres) para os resultados de busca.</Form.Text>
                    </Form.Group>
                </Tab>
            )}
        </Tabs>
    );
};

export default LandPageServicosTab;
